import { useState } from 'react';
import { Loader2, Eye, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { invoicingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtAmount(v, currency) {
  if (v == null || v === '') return '-';
  const sym = currency === 'EUR' ? '\u20ac' : currency || '';
  return `${sym}${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function computeLineTotal(line) {
  const qty = Number(line.quantity) || 0;
  const unitPrice = Number(line.raw_currency_unit_price ?? line.unit_price) || 0;
  const amount = Number(line.currency_amount ?? line.amount) || 0;
  // Prefer qty * unit_price if available, fall back to currency_amount
  if (unitPrice !== 0) return qty * unitPrice;
  return amount;
}

function ReconciliationRow({ lines, invoiceAmount, currency }) {
  if (!lines?.length) return null;

  const lineSum = lines.reduce((acc, line) => acc + computeLineTotal(line), 0);
  const total = Number(invoiceAmount) || 0;
  const diff = Math.abs(lineSum - total);
  const mismatch = diff > 0.01;
  const hasCommission = lines.some(
    (l) => (l.label || '').toLowerCase().includes('commission') && Number(l.quantity) < 0
  );

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs font-medium pt-1 border-t">
        <span>Sum of lines</span>
        <span className="tabular-nums">{fmtAmount(lineSum, currency)}</span>
      </div>
      <div className="flex justify-between text-xs">
        <span>Pennylane total</span>
        <span className="tabular-nums">{fmtAmount(total, currency)}</span>
      </div>
      {mismatch && (
        <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/50 rounded px-2 py-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          Line sum ({fmtAmount(lineSum, currency)}) does not match Pennylane total ({fmtAmount(total, currency)}).
          Difference: {fmtAmount(diff, currency)}.
        </div>
      )}
      {!hasCommission && total > 0 && (
        <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/50 rounded px-2 py-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          No commission deduction line. Total may be gross (not net revenue).
        </div>
      )}
    </div>
  );
}

export function InvoiceViewPanel({ entityId }) {
  const [invoiceId, setInvoiceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchInvoice = async () => {
    if (!invoiceId.trim()) {
      toast.error('Enter a Pennylane invoice ID');
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const res = await invoicingAPI.viewInvoice(entityId, invoiceId.trim());
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Eye className="h-5 w-5 text-blue-500" />
          View Invoice (Live)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Fetch an invoice directly from Pennylane by ID. Read-only, reflects edits/finalisation done in Pennylane.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-sm font-medium text-muted-foreground block mb-1">
              Pennylane Invoice ID
            </label>
            <input
              type="text"
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              placeholder="e.g. 12345678"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && fetchInvoice()}
            />
          </div>
          <Button onClick={fetchInvoice} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            View
          </Button>
        </div>

        {data && (
          <div className="space-y-3 text-sm rounded-lg border p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Invoice #</p>
                <p className="font-medium">
                  {data.draft
                    ? <span className="text-muted-foreground italic">No legal invoice number until finalized</span>
                    : (data.invoice_number || '-')}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <p className="font-medium">{data.draft ? 'Draft' : (data.status || '-')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="font-medium">{data.date || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Currency</p>
                <p className="font-medium">{data.currency || '-'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="font-medium">
                  {data.amount != null ? fmtAmount(data.amount, data.currency) : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-medium">
                  {data.customer?.name
                    || data.customer?.first_name
                    || (typeof data.customer === 'string' ? data.customer : null)
                    || '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Guest</p>
                <p className="font-medium">{data.guest_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Uplisting ID</p>
                <p className="font-medium font-mono">{data.uplisting_id || '-'}</p>
              </div>
            </div>

            {/* Lines */}
            {data.invoice_lines?.length > 0 ? (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Invoice Lines</p>
                <div className="rounded border overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-2 py-1 font-medium">Label</th>
                        <th className="text-right px-2 py-1 font-medium">Qty</th>
                        <th className="text-right px-2 py-1 font-medium">Unit Price</th>
                        <th className="text-right px-2 py-1 font-medium">Amount</th>
                        <th className="text-right px-2 py-1 font-medium">VAT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoice_lines.map((line, i) => (
                        <tr key={i} className="border-b">
                          <td className="px-2 py-1">{line.label || line.description || '-'}</td>
                          <td className="px-2 py-1 text-right">{line.quantity}</td>
                          <td className="px-2 py-1 text-right tabular-nums">
                            {(line.raw_currency_unit_price ?? line.unit_price) != null
                              ? fmtAmount(line.raw_currency_unit_price ?? line.unit_price, data.currency)
                              : '-'}
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums">
                            {fmtAmount(computeLineTotal(line), data.currency)}
                          </td>
                          <td className="px-2 py-1 text-right">{line.vat_rate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ReconciliationRow
                  lines={data.invoice_lines}
                  invoiceAmount={data.amount}
                  currency={data.currency}
                />
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50 p-3 text-xs text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium">No line items returned by Pennylane for this {data.draft ? 'draft' : 'invoice'}.</span>
              </div>
            )}

            {/* Links */}
            <div className="flex gap-3">
              {data.pennylane_url && (
                <a
                  href={data.pennylane_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 dark:text-blue-400 underline"
                >
                  Open in Pennylane
                </a>
              )}
              {data.pdf_url && (
                <a
                  href={data.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 dark:text-blue-400 underline"
                >
                  Download PDF
                </a>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
